import { useCallback, useEffect, useState } from 'react';
import useMap from '../hooks/useMap';

export type SvgDefinition = {
  name: string;
  svgString: string;
  height: number;
  width: number;
};

/**
 * Hook to add svgs to mapbox as images to be used by markers/layers/etc
 * @param svgDefinitions Array of objects defining each svg image
 * name:
 *   The identifier used by mapbox
 * svgString:
 *   An svg definition, _as a string_ - see curatedWaypointSvg.ts for an example.
 *   If we try to use an actual svg file and pass in svgFile.src, for example, next
 *   recognizes that as an asset so the resulting src string is a url to our CDN where the
 *   svg is being hosted (it works locally, but not for an actual deployment).
 *   This would require us to then call map.loadImage which introduces race conditions
 *   and extra waiting time. Instead, if we just keep the svg definition as a string
 *   and convert it to a base-64 encoded string, we can use that directly as the image
 *   src and skip the call to loadImage altogether.
 * height:
 *   The height of the rendered image on the map
 * width:
 *   The width of the rendered image on the map
 * @returns True once the images have been added to mapbox
 */
const useSvgImages = (svgDefinitions: SvgDefinition[], mapId?: string) => {
  const map = useMap(mapId);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set([]));

  const loadImage = useCallback(
    ({ name, svgString, height, width }: SvgDefinition) => {
      if (!map || map.hasImage(name)) {
        setLoadedImages(images => {
          const newSet = new Set(images);
          newSet.add(name);
          return newSet;
        });
        return;
      }

      // Doubling the image size and using a pixelRatio of 2 gives us a much higher-res image
      const img = new Image(width * 2, height * 2);

      img.onload = () => {
        map.addImage(name, img, { pixelRatio: 2 });
        setLoadedImages(images => {
          const newSet = new Set(images);
          newSet.add(name);
          return newSet;
        });
      };

      const src = `data:image/svg+xml;base64,${window.btoa(svgString)}`;
      img.src = src;
    },
    [map]
  );

  useEffect(() => {
    if (!map) {
      return;
    }
    svgDefinitions.forEach(loadImage);

    // When the map's base style is changed, all sources and layers are lost.
    // style.load is triggered when a new base style is loaded, so re-add the svg images whenever this happens.
    // https://github.com/mapbox/mapbox-gl-js/issues/4006
    map.on('style.load', () => {
      setLoadedImages(new Set([]));
      svgDefinitions.forEach(loadImage);
    });
  }, [loadImage, map, svgDefinitions]);

  return { imagesAreLoaded: svgDefinitions.every(({ name }) => loadedImages.has(name)), loadImage };
};

export function preloadSvg(svgString: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = `data:image/svg+xml;base64,${window.btoa(svgString)}`;
  });
}

export default useSvgImages;
